// Sugalarm — Cycle 354: the sensor-pause prompt.
//              Cycle 375: the No-Data SEQUENCE.
//
// The household's answer to a dark feed: "Is <name> away or changing the
// sensor?" Renders in two modes:
//
//  - `strip` (Now screen, all variants): mounts only while the feed is
//    dark (the No-Data hero is showing) — a compact banner strip, NEVER
//    a modal, and always below the alarm surfaces in z-order (it is
//    normal-flow content).
//  - `settings` (Settings → Sensor/CGM card): the declare-ahead control —
//    always visible, so a sensor change can be declared BEFORE pulling
//    the sensor and no blackout page ever fires.
//
// Any audience member may answer (the endpoints authorize via the
// accessible-member set; the kiosk's device token works too). The hold is
// server-clamped (sensor change ≤3h, away ≤24h — never permanent), rides
// the settings_update broadcast so every device converges, auto-expires,
// and auto-clears when data returns. It gates ONLY the cycle-353
// blackout pages — glucose alarms are untouched by construction.
//
// ── Cycle 375: the three-state sequence ───────────────────────────────
//
//   ask  ──(pick a duration, POST resolves)──▶  held
//   held ──(Resume alerts, feed still dark)──▶  watching
//   held ──(Resume alerts, fresh reading)────▶  (strip clears)
//   any  ──(a fresh reading arrives)─────────▶  (strip clears, purged)
//
// Three properties are load-bearing, and each fixes a shipped defect:
//
//  1. **The confirmation is driven by the POST RESPONSE, not by the
//     settings refresh.** `setSensorPause` already returns the stored
//     `sensor_pause`; cycle 354 discarded it and waited for
//     `refresh()` → `GET /api/settings` to round-trip, so the strip sat
//     on the ask-buttons through the whole refresh (and showed nothing
//     at all if the refresh was slow or failed). The response is now
//     held locally as `optimisticPause` and rendered immediately, then
//     reconciled against the server copy — no flicker, no blank step.
//
//  2. **Resume never re-enters the ask loop.** Pressing Resume while the
//     feed is still dark lands on a distinct "watching for data" state.
//     Re-asking "changing sensor / away?" the instant somebody said "no,
//     resume" is the loop this cycle removes.
//
//  3. **A fresh reading purges everything.** One authoritative teardown
//     keyed on `dark` going false clears every local artifact, so a LATER
//     gap always begins a clean sequence with the ask-buttons — never a
//     stale "paused until …" or "watching" left over from last time.
//     This is deliberately independent of the server's own auto-clear, so
//     the banner is correct even if the persisted hold lags.

import { useEffect, useState } from "react";

import { clearSensorPause, setSensorPause } from "../../api/members";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import {
  ASK_EYEBROW,
  AWAY_DURATION_CHOICES,
  AWAY_EYEBROW,
  AWAY_PROMPT,
  PAUSE_HELD_EYEBROW,
  PAUSE_HELD_REASSURANCE,
  RESUMED_EYEBROW,
  RESUMED_HEADLINE,
  WATCHING_BODY,
  WATCHING_EYEBROW,
  WATCHING_HEADLINE,
  askPrompt,
  isPauseActive,
  isSamePause,
  pauseHeldHeadlineParts,
  pauseReasonEmoji,
  pauseUntilLabel,
} from "../../lib/sensorPause";
import { useSettings } from "../../lib/settingsContext";
import type { SensorPause } from "../../lib/types";
import styles from "./SensorPausePrompt.module.css";

interface Props {
  memberId: string | null;
  memberName: string;
  /** The feed is currently dark (the No-Data hero is showing). The
   *  false→true edge starts a sequence; the true→false edge (a fresh,
   *  non-stale reading) purges it. */
  dark: boolean;
  mode: "strip" | "settings";
  h24: boolean;
  now: number;
  /** Drives layout only: kiosk/web render a single horizontal row, mobile
   *  a centered stacked column. Omitted → the stacked column (the safe
   *  default for an unknown, possibly narrow surface). */
  variant?: FrameVariant;
}

export function SensorPausePrompt({
  memberId,
  memberName,
  dark,
  mode,
  h24,
  now,
  variant,
}: Props) {
  const { settings, refresh } = useSettings();
  const [busy, setBusy] = useState(false);
  const [awayOpen, setAwayOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Cycle 375: the pause we just created, rendered until the server copy
  // catches up (deliverable 2 — no flicker back to the ask-buttons).
  const [optimisticPause, setOptimisticPause] = useState<SensorPause | null>(
    null,
  );
  // Cycle 375: the specific hold we successfully DELETEd, held so we can
  // ignore it while it lingers in our (stale) settings snapshot — otherwise
  // the confirmation flashes back between the DELETE resolving and the
  // settings_update landing.
  //
  // THIS IS THE CLEARED HOLD'S IDENTITY, NOT A BOOLEAN LATCH, and the
  // difference is safety-relevant. A boolean would self-heal only on the
  // server reporting `null`; if another caregiver set a NEW hold in the
  // window between our DELETE and our refresh, the snapshot would go
  // non-null WITHOUT ever passing through null, the latch would stay
  // stuck, and this device would render "Watching for data" — claiming
  // the blackout page is armed — while the server was in fact suppressing
  // it for up to 24 hours. Matching on identity means any hold that isn't
  // the one we cleared renders as the hold it is.
  const [clearedPause, setClearedPause] = useState<SensorPause | null>(null);
  // Cycle 375: deliverable 3 — "watching for data", entered by resuming
  // while the feed is still dark. Purged on data return.
  const [watching, setWatching] = useState(false);
  // Cycle 375.1: resumed while the feed is LIVE — the quiet confirmed state
  // that replaces bouncing back to the ask affordance.
  const [resumedLive, setResumedLive] = useState(false);

  const rawServerPause: SensorPause | null = settings?.sensor_pause ?? null;
  const serverPause = isSamePause(clearedPause, rawServerPause)
    ? null
    : rawServerPause;
  const serverActive = isPauseActive(serverPause, now);
  const pause: SensorPause | null = serverActive
    ? serverPause
    : isPauseActive(optimisticPause, now)
      ? optimisticPause
      : null;
  const held = pause != null;

  // Reconcile: once the server copy carries the hold, the optimistic one
  // has done its job. Dropping it here keeps `pause` non-null across the
  // handover (server wins on the same render), so there is no gap.
  useEffect(() => {
    if (serverActive && optimisticPause != null) setOptimisticPause(null);
  }, [serverActive, optimisticPause]);

  // Drop the suppression as soon as the server stops reporting the exact
  // hold we cleared — whether it now reports nothing (the normal case) or
  // a DIFFERENT hold (someone else declared one meanwhile, which must be
  // shown, not masked).
  useEffect(() => {
    if (clearedPause != null && !isSamePause(clearedPause, rawServerPause)) {
      setClearedPause(null);
    }
  }, [clearedPause, rawServerPause]);

  // Switching the active member switches the subject entirely: this
  // component is not remounted (no `key`), and the settings context swaps
  // underneath it, so without this reset the previous member's optimistic
  // hold or watching state would be rendered against the NEW member. The
  // data-return purge below can't cover it — if both members' feeds are
  // dark, `dark` never changes and the effect never fires.
  useEffect(() => {
    setOptimisticPause(null);
    setClearedPause(null);
    setWatching(false);
    setResumedLive(false);
    setAwayOpen(false);
    setError(null);
  }, [memberId]);

  // ── Cycle 375 deliverable 5: the authoritative data-return purge ─────
  // Keyed on a fresh (non-stale) reading arriving — `dark` going false.
  // Tears down EVERY local no-data artifact in one place: the optimistic
  // hold, the clear-suppression, the watching state, the half-open away
  // picker, and any stale error. A later gap therefore starts clean.
  //
  // Scoped to `strip`: the Settings declare-ahead control is not part of
  // the no-data sequence and is never dark, so purging there would wipe
  // its confirmation the moment it appeared.
  useEffect(() => {
    if (mode !== "strip" || dark) return;
    setOptimisticPause(null);
    setClearedPause(null);
    setWatching(false);
    setResumedLive(false);
    setAwayOpen(false);
    setError(null);
  }, [mode, dark]);

  // ── Render guards (after every hook — never before) ──────────────────
  if (memberId == null) return null;
  // The strip earns its place only while the feed is dark. A fresh reading
  // clears it even if a hold is still persisted server-side (deliverable 5:
  // "or a fresh reading arrives, the banner ... must be gone").
  if (mode === "strip" && !dark) return null;

  const firstName = memberName.split(" ")[0] || memberName;
  const stacked = variant !== "kiosk" && variant !== "web";
  const shellClass = `${styles.strip} ${
    stacked ? styles.stacked : styles.row
  }`;

  async function pauseFor(
    reason: SensorPause["reason"],
    minutes?: number,
  ): Promise<void> {
    if (busy || memberId == null) return;
    setBusy(true);
    setError(null);
    const r = await setSensorPause(memberId, reason, minutes);
    if (r.ok) {
      // Deliverable 2: the confirmation is the immediate next step. Render
      // straight from the response — do NOT wait on `refresh()`.
      setOptimisticPause(r.data.sensor_pause);
      setClearedPause(null);
      setWatching(false);
      setAwayOpen(false);
    } else {
      // Never a false confirmation: stay on the ask affordance.
      setError("Couldn't pause alerts — try again.");
    }
    setBusy(false);
    // Reconcile against the server in the background; the transition above
    // has already happened, so a slow settings round-trip can't stall it.
    void refresh();
  }

  async function resume(): Promise<void> {
    if (busy || memberId == null) return;
    setBusy(true);
    setError(null);
    const r = await clearSensorPause(memberId);
    if (r.ok) {
      setOptimisticPause(null);
      setClearedPause(pause);
      setAwayOpen(false);
      // Deliverable 3: still dark → the watching state, NOT the ask loop.
      // Feed already live → the quiet "alerts active" state (375.1); in
      // strip mode the purge then clears the whole strip anyway, so this
      // only ever surfaces on the Settings declare-ahead card.
      setWatching(dark);
      setResumedLive(!dark);
    } else {
      // The hold is still standing — stay on the confirmation rather than
      // claim we are watching when alerts are in fact still paused.
      setError("Couldn't resume alerts — try again.");
    }
    setBusy(false);
    void refresh();
  }

  // Every state shares one anatomy (the mockup's): a lead icon tile, a body
  // (eyebrow → headline/prompt → supporting line), and an optional actions
  // group. Keeping the shape identical across states is what makes the
  // sequence read as one strip changing its mind rather than four
  // different banners.
  function shell(
    tone: "warn" | "accent",
    icon: React.ReactNode,
    body: React.ReactNode,
    actions?: React.ReactNode,
  ) {
    return (
      <div
        className={`${shellClass} ${tone === "accent" ? styles.watching : styles.held}`}
        role="status"
      >
        <span className={styles.leadicon} aria-hidden>
          {icon}
        </span>
        <div className={styles.text}>{body}</div>
        {actions ? <div className={styles.actions}>{actions}</div> : null}
        {error ? <span className={styles.error}>{error}</span> : null}
      </div>
    );
  }

  const resumeBtn = (
    <button
      type="button"
      className={`${styles.btn} ${styles.btnResume}`}
      disabled={busy}
      onClick={() => void resume()}
    >
      Resume alerts
    </button>
  );

  // ── 1. Held: reason-aware confirmation ───────────────────────────────
  if (held && pause) {
    const parts = pauseHeldHeadlineParts(
      pause.reason,
      pauseUntilLabel(pause, h24),
    );
    return shell(
      "warn",
      pauseReasonEmoji(pause.reason),
      <>
        <div className={styles.eyebrow}>{PAUSE_HELD_EYEBROW}</div>
        <div className={styles.headline}>
          {parts.lead}
          <span className={styles.until}>{parts.until}</span>
          {parts.tail}
        </div>
        <div className={styles.reassure}>{PAUSE_HELD_REASSURANCE}</div>
      </>,
      resumeBtn,
    );
  }

  // ── 2. Watching for data ─────────────────────────────────────────────
  // Deliberately actionless: there is nothing to resume yet, and we do NOT
  // re-prompt the pause ask. Its own calm accent variant so it never reads
  // as "still paused".
  if (watching) {
    return shell(
      "accent",
      "📡",
      <>
        <div className={styles.eyebrow}>
          <span className={styles.watchdot} aria-hidden />
          {WATCHING_EYEBROW}
        </div>
        <div className={styles.headline}>{WATCHING_HEADLINE}</div>
        <div className={styles.reassure}>{WATCHING_BODY}</div>
      </>,
    );
  }

  // ── 3. Resumed while the feed is LIVE (cycle 375.1) ──────────────────
  // The Settings declare-ahead card can't "clear" the way the strip does,
  // and re-offering the ask here is the loop this sequence exists to
  // remove. Actionless and accurate: nothing is paused, and there IS data.
  if (resumedLive) {
    return shell(
      "accent",
      "✅",
      <>
        <div className={styles.eyebrow}>{RESUMED_EYEBROW}</div>
        <div className={styles.headline}>{RESUMED_HEADLINE}</div>
      </>,
    );
  }

  // ── 4. Pick a duration (away) ────────────────────────────────────────
  if (awayOpen) {
    return shell(
      "warn",
      "🏖️",
      <>
        <div className={styles.eyebrow}>{AWAY_EYEBROW}</div>
        <div className={styles.prompt}>{AWAY_PROMPT}</div>
      </>,
      AWAY_DURATION_CHOICES.map((c) => (
        <button
          key={c.minutes}
          type="button"
          className={`${styles.btn} ${styles.btnPrimary}`}
          disabled={busy}
          onClick={() => void pauseFor("away", c.minutes)}
        >
          {c.label}
        </button>
      )),
    );
  }

  // ── 5. The ask affordance ────────────────────────────────────────────
  // The two buttons and their duration choices are the preserved cycle-354
  // affordance; only the surrounding wording follows the mockup.
  return shell(
    "warn",
    "📡",
    <>
      <div className={styles.eyebrow}>{ASK_EYEBROW}</div>
      <div className={styles.prompt}>
        {mode === "strip"
          ? askPrompt(firstName)
          : `Pause "sensor dark" alerts — before a sensor change or time away:`}
      </div>
    </>,
    <>
      <button
        type="button"
        className={`${styles.btn} ${styles.btnPrimary}`}
        disabled={busy}
        onClick={() => void pauseFor("sensor_change")}
      >
        🔄 Changing sensor
      </button>
      <button
        type="button"
        className={`${styles.btn} ${styles.btnPrimary}`}
        disabled={busy}
        onClick={() => setAwayOpen(true)}
      >
        🏖️ {firstName} is away…
      </button>
    </>,
  );
}
